import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@mui/material";
import { usePageMetadata } from "../../context/PageMetadataContext";

import { useLanguages } from "../../context/LanguagesContext";
import { useToast } from "../../context/ToastContext";
import {
  getPortalUserAttribute,
  getMyProfile,
  updateMyProfile,
  changePassword,
} from "../../services/api/apiClient";
import {
  GetPortalUserAttributeResult,
  GetUserProfileResult,
  UpdateProfileBodyParams,
  ProfileAttributeUpdateType,
} from "../../services/api/types/users/users";
import EmailIcon from "@mui/icons-material/Email";
import { Link, Box } from "@mui/material";
import { ResetPasswordDialog } from "./components/ResetPasswordDialog";
import {
  PersonalInfoSection,
  PersonalInfoFieldConfig,
} from "./components/PersonalInfoSection";
import {
  MyDetailsSection,
  MyDetailsFieldConfig,
} from "./components/MyDetailsSection";
import { useCustomisation } from "../../context/CustomisationContext";
import { resolvePageStyle } from "../../utils/pageStyleResolver";
import { getShadeColour } from "../../utils/helpers";
import { extractErrorMessage } from "../../utils/errorUtils";
import { hasSomeCustomisation } from "../../utils/helpers";
import { usePageCustomisation } from "../../hooks/usePageCustomisation";
import { useTranslate } from "../../hooks/useTranslate";
import { useAuth } from "../../context/AuthContext";
import { MyProfileSkeleton } from "./components/MyProfileSkeleton";

type AttributeValueType = string | number | boolean | string[] | null;

interface PersonalInfoFormValues {
  firstName: string;
  lastName: string;
  email: string;
}

type PersonalInfoErrors = {
  [key: string]: string;
};

type ValidateOptions = {
  fieldKey?: string;
  value?: string;
  label?: string;
  required?: boolean;
};

const MyProfile = () => {
  const { auth, dispatch } = useAuth();
  const { languages } = useLanguages();
  usePageCustomisation("PROFILE");
  const { showToast } = useToast();
  const t = useTranslate("my-profile");
  const user = auth.user;
  const { customisations, getCustomisationValue } = useCustomisation();

  const {
    PROFILE: { buttonsSettings: profilebuttonssettings },
    GPC: { buttonsSettings: gpcbuttonsettings },
  } = customisations.customisations;
  const primaryButtonTextColor =
    profilebuttonssettings?.profileBtnPrimaryTextColor ??
    gpcbuttonsettings?.gpcBtnPrimaryTextColor;
  const primaryButtonBgColor =
    profilebuttonssettings?.profileBtnPrimaryBgColor ??
    gpcbuttonsettings?.gpcBtnPrimaryBgColor;
  const linkTextColor =
    profilebuttonssettings?.profileLinkColor ?? gpcbuttonsettings?.gpcLinkColor;

  const [personalInfo, setPersonalInfo] = useState<PersonalInfoFormValues>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
  });
  const [uploadedThumbnailPath, setUploadedThumbnailPath] = useState<
    string | null
  >(null);
  const [profileThumbnailUrl, setProfileThumbnailUrl] = useState<string | null>(
    null,
  );
  const [profilePictureKey, setProfilePictureKey] = useState(0);
  const [portalAttributes, setPortalAttributes] = useState<
    GetPortalUserAttributeResult[]
  >([]);
  const [attributeValues, setAttributeValues] = useState<
    Record<string, AttributeValueType>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userDetails, setUserDetails] = useState<GetUserProfileResult | null>(
    null,
  );
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [personalInfoErrors, setPersonalInfoErrors] =
    useState<PersonalInfoErrors>({});
  const portalUid = user?.PortalUid;
  const userPortalUid = user?.UserPortalUid;

  usePageMetadata({
    title: t("page", "title"),
    breadcrumbs: [],
    pageStyle: resolvePageStyle("PROFILE", getCustomisationValue),
  });

  // Convert attribute value based on data type
  const convertAttributeValue = (
    value: string | null,
    dataType: string,
    isLineManager?: boolean,
  ): AttributeValueType => {
    if (value === null || value === undefined || value === "") return null;

    const type = dataType.toLowerCase();

    // Handle isLineManager string attributes (parse pipe-separated string to array)
    if (isLineManager && type === "string") {
      return value.split("|").filter(Boolean);
    }

    if (type === "number") {
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }
    if (type === "boolean") {
      return value.toLowerCase() === "true" || value === "1";
    }
    return value;
  };

  // firstName/lastName/email are all read-only on this page (see `disabled: true`
  // below), so there is no user-editable required field left to validate here.
  const validatePersonalInfo = ({ fieldKey }: ValidateOptions = {}): boolean => {
    if (fieldKey) {
      setPersonalInfoErrors((prev) => ({ ...prev, [fieldKey]: "" }));
    }
    return true;
  };

  // Fetch profile and attributes in parallel
  useEffect(() => {
    const fetchData = async () => {
      if (!portalUid || !userPortalUid || !languages.selectedLanguage) return;

      setLoading(true);
      try {
        const [profileResponse, attributesResponse] = await Promise.all([
          getMyProfile(portalUid, userPortalUid),
          getPortalUserAttribute(portalUid, languages.selectedLanguage, false),
        ]);

        // @ts-ignore
        const attrs = attributesResponse.rows || [];
        setPortalAttributes(attrs);
        setUserDetails(profileResponse);
        setPersonalInfo({
          firstName: profileResponse.firstName || "",
          lastName: profileResponse.lastName || "",
          email: profileResponse.email || "",
        });
        setProfileThumbnailUrl(profileResponse.thumbnailUrl || null);

        // Map attribute values
        if (profileResponse.attributes?.length && attrs.length) {
          const values: Record<string, AttributeValueType> = {};
          profileResponse.attributes.forEach((attr) => {
            if (attr.attributeValue != null) {
              const portalAttr = attrs.find(
                (a: GetPortalUserAttributeResult) =>
                  a.attributeKey === attr.attributeKey,
              );
              values[attr.attributeKey] = convertAttributeValue(
                attr.attributeValue,
                portalAttr?.dataType || attr.dataType,
                portalAttr?.isLineManager,
              );
            }
          });
          setAttributeValues(values);
        } else {
          setAttributeValues({});
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showToast("Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid, userPortalUid, languages.selectedLanguage]);

  // Get available, sorted attributes from profile
  const displayAttributes = useMemo(() => {
    const visible = portalAttributes.filter(
      (attr) => attr.isAvailable !== false && attr.isActive !== false,
    );

    // Add profile attributes not in portal attributes
    if (userDetails?.attributes) {
      const portalKeys = new Set(portalAttributes.map((a) => a.attributeKey));
      userDetails.attributes.forEach((profileAttr) => {
        if (!portalKeys.has(profileAttr.attributeKey)) {
          visible.push({
            portalUserAttributeUid: profileAttr.PortalUserAttributeUid,
            attributeName: profileAttr.attributeKey,
            "portalUserAttributeTranslations.attributeName":
              profileAttr.attributeKey,
            attributeKey: profileAttr.attributeKey,
            dataType: profileAttr.dataType,
            isRequired: profileAttr.isRequired,
            isEditable: profileAttr.isEditable,
            isAvailable: true,
            isActive: true,
            attributeOrder: parseInt(profileAttr.attributeOrder) || 999,
          } as GetPortalUserAttributeResult);
        }
      });
    }

    return visible.sort(
      (a, b) => (a.attributeOrder || 0) - (b.attributeOrder || 0),
    );
  }, [portalAttributes, userDetails]);

  const handleAttributeChange = useCallback(
    (key: string, value: AttributeValueType) => {
      setAttributeValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handlePersonalInfoChange = useCallback(
    (
      key: keyof typeof personalInfo,
      value: string,
      label?: string,
      required?: boolean,
    ) => {
      setPersonalInfo((prev) => ({
        ...prev,
        [key]: value,
      }));

      validatePersonalInfo({
        fieldKey: key,
        value,
        label,
        required,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleThumbnailPathChange = (path: string | null) => {
    setUploadedThumbnailPath(path);
  };

  const handleResetPassword = useCallback(() => {
    setResetPasswordDialogOpen(true);
  }, []);

  const handleUpdatePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    try {
      await changePassword({ oldPassword: currentPassword, newPassword });
      showToast("Password updated successfully", "success");
    } catch (error: any) {
      // The axios response interceptor already surfaces the backend's
      // actual error message via a global toast (e.g. "Attempt limit
      // exceeded..."); showing a second, weaker one here just overwrites
      // it with a generic fallback since both toasts share one slot.
      throw error;
    }
  };

  const handleSaveProfile = async () => {
    const isFormValid = validatePersonalInfo();

    if (!isFormValid) return;
    if (!user) return;

    if (!user.PortalUid) return;

    setSaving(true);
    try {
      // Convert attribute value to API format
      const toApiString = (v: AttributeValueType): string | null => {
        if (v === "" || v === null || v === undefined) return null;
        if (typeof v === "boolean") return v ? "true" : "false";
        return String(v);
      };

      // Prepare attributes to update
      const additionalAttributes: ProfileAttributeUpdateType[] =
        displayAttributes
          .filter((attr) => attributeValues[attr.attributeKey] !== undefined)
          .map((attr) => {
            const value = attributeValues[attr.attributeKey];

            // Handle isLineManager string attributes (pipe separated)
            if (
              attr.isLineManager &&
              attr.dataType === "string" &&
              Array.isArray(value)
            ) {
              return {
                PortalUserAttributeUid: attr.portalUserAttributeUid,
                attributeKey: attr.attributeKey,
                attributeValue: value.join("|"),
              };
            }

            return {
              PortalUserAttributeUid: attr.portalUserAttributeUid,
              attributeKey: attr.attributeKey,
              attributeValue: toApiString(value),
            };
          });

      const updateBody: UpdateProfileBodyParams = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        email: personalInfo.email,
        ...(uploadedThumbnailPath && { thumbnailUrl: uploadedThumbnailPath }),
        ...(additionalAttributes.length > 0 && { additionalAttributes }),
      };
      const profileResponse = await updateMyProfile(
        user.PortalUid,
        user.UserPortalUid,
        updateBody,
      );
      setUserDetails((prev) =>
        prev ? { ...prev, ...profileResponse } : profileResponse,
      );
      setPersonalInfo((prev) => ({
        firstName: profileResponse.firstName ?? prev.firstName,
        lastName: profileResponse.lastName ?? prev.lastName,
        email: profileResponse.email ?? prev.email,
      }));
      setProfileThumbnailUrl(
        (prev) =>
          profileResponse.thumbnailUrl ??
          uploadedThumbnailPath ??
          prev ??
          user?.thumbnail ??
          null,
      );
      const resolvedThumbnailUrl =
        profileResponse.thumbnailUrl ??
        uploadedThumbnailPath ??
        profileThumbnailUrl ??
        user?.thumbnail ??
        null;
      setUploadedThumbnailPath(null);
      setProfilePictureKey((prev) => prev + 1); // Reset profile picture component

      // Update attribute values (merge so a partial API response does not wipe the form)
      if (profileResponse.attributes?.length && portalAttributes.length) {
        setAttributeValues((prev) => {
          const next = { ...prev };
          profileResponse.attributes!.forEach((attr) => {
            if (attr.attributeValue != null) {
              const portalAttr = portalAttributes.find(
                (a: GetPortalUserAttributeResult) =>
                  a.attributeKey === attr.attributeKey,
              );
              next[attr.attributeKey] = convertAttributeValue(
                attr.attributeValue,
                portalAttr?.dataType || attr.dataType,
                portalAttr?.isLineManager,
              );
            }
          });
          return next;
        });
      }

      // Update user context
      dispatch({
        type: "SET_USER",
        payload: {
          ...user,
          firstName: profileResponse.firstName || user.firstName,
          lastName: profileResponse.lastName || user.lastName,
          thumbnail: resolvedThumbnailUrl || user.thumbnail,
        },
      });

      showToast("Profile updated successfully", "success");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      showToast(
        extractErrorMessage(
          error,
          "Failed to update profile. Please try again.",
        ),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // Configure personal info fields dynamically
  const personalInfoFields: PersonalInfoFieldConfig[] = useMemo(
    () => [
      {
        key: "firstName",
        label: t("page", "firstName"),
        value: personalInfo.firstName,
        onChange: (value) =>
          handlePersonalInfoChange("firstName", value, t("page", "firstName")),
        placeholder: t("page", "firstNamePlaceholder"),
        error: personalInfoErrors.firstName,
        disabled: true,
      },
      {
        key: "lastName",
        label: t("page", "lastName"),
        value: personalInfo.lastName,
        onChange: (value) =>
          handlePersonalInfoChange("lastName", value, t("page", "lastName")),
        placeholder: t("page", "lastNamePlaceholder"),
        error: personalInfoErrors.lastName,
        disabled: true,
      },
      {
        key: "email",
        label: t("page", "emailAddress"),
        value: personalInfo.email,
        onChange: (value) => handlePersonalInfoChange("email", value),
        type: "email",
        placeholder: "",
        disabled: true,
        startAdornment: (
          <EmailIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        ),
      },
      {
        key: "resetPassword",
        label: "",
        value: "",
        onChange: () => {},
        render: () => (
          <Link
            component="button"
            onClick={handleResetPassword}
            sx={{
              color: linkTextColor,
              cursor: "pointer",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
              alignSelf: "flex-start",
              "&:hover": {
                textDecoration: "underline !important",
              },
            }}
          >
            {t("page", "resetPassword")}
          </Link>
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      personalInfo,
      handlePersonalInfoChange,
      handleResetPassword,
      personalInfoErrors,
      t,
    ],
  );

  // Configure my details fields dynamically
  const myDetailsFields: MyDetailsFieldConfig[] = useMemo(() => {
    if (!displayAttributes.length) return [];

    return displayAttributes.map((attr) => {
      return {
        key: attr.portalUserAttributeUid || attr.attributeKey || "",
        attribute: attr,
        value: attributeValues[attr.attributeKey] ?? null,
        onChange: handleAttributeChange,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayAttributes, attributeValues, userDetails, handleAttributeChange]);

  const isProfileLoaded = hasSomeCustomisation(
    customisations.customisations,
    "PROFILE",
  );

  if (!isProfileLoaded) return <MyProfileSkeleton />;

  return (
    <Box sx={{ maxWidth: 1440, margin: "0 auto" }}>
      <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
        <PersonalInfoSection
          title={t("page", "personalInfoTitle")}
          fields={personalInfoFields}
          profileThumbnailUrl={profileThumbnailUrl}
          userThumbnail={user?.thumbnail || null}
          portalUid={portalUid}
          profilePictureKey={profilePictureKey}
          onThumbnailPathChange={handleThumbnailPathChange}
        />

        <MyDetailsSection
          title={t("page", "myDetailsTitle")}
          loading={loading}
          fields={myDetailsFields}
          emptyMessage={t("page", "noAttributes")}
        />

        <Button
          onClick={handleSaveProfile}
          sx={{
            mt: 2,
            alignSelf: "center",
            backgroundColor: `${primaryButtonBgColor} !important`,
            color: primaryButtonTextColor,
            minWidth: 120,
            whiteSpace: "nowrap",
            "&:hover": {
              // textDecoration: 'underline'
              bgcolor: `${getShadeColour(primaryButtonBgColor!, -4)} !important`,
            },
          }}
        >
          {saving ? t("page", "saving") : t("page", "saveChanges")}
        </Button>

        <ResetPasswordDialog
          open={resetPasswordDialogOpen}
          onClose={() => setResetPasswordDialogOpen(false)}
          onUpdatePassword={handleUpdatePassword}
        />
      </Box>
    </Box>
  );
};

export default MyProfile;
